// Full-name tooltip for truncated vehicle-selector options (Make / Year /
// Model / Variant). The dropdown panel stays the SAME width as its field with
// one ellipsised line per option; an option whose text is actually cut off
// (measured: scrollWidth > clientWidth, never guessed from length) gets a small
// info icon, an aria-label with the full name (no native title, so the browser
// tooltip never doubles up), and a tooltip
// styled like the ALTERNATIVE PARTS (i) badge popover on result cards.
//
// The tooltip is a single fixed-position element on <body> so the panel's
// overflow can never clip it; it flips below / shifts sideways to stay in the
// viewport. Mouse: hover a truncated row or its icon. Keyboard: arrow onto a
// truncated row. Touch: tap the icon (does NOT select the row); tap elsewhere
// hides it.
//
// Kept in sync by hand with the inline copy in
// app/public/js/storefront-vehicle-selector.js (standalone legacy script).

var TIP_ID = 'gw-trunc-tip';
var TIP_BG = '#111827';
var EDGE = 8;   // min gap from the viewport edge
var OFFSET = 6; // gap between tooltip and anchor

/** Same "i" glyph as the ALTERNATIVE PARTS badge. */
export var ICON_INFO = '<svg width="14" height="14" viewBox="0 0 16 16" fill="currentColor" aria-hidden="true"><path d="M8 1a7 7 0 100 14A7 7 0 008 1zm.75 10.5h-1.5v-5h1.5v5zm0-6.5h-1.5V3.5h1.5V5z"/></svg>';

/** Inline styles for one-line ellipsised text (independent of theme CSS). */
export var ELLIPSIS_CSS = 'display:block;flex:1 1 auto;min-width:0;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;';

var _anchor = null;
var _via = null; // 'mouse' | 'kbd' | 'touch'
var _lastPointer = 'mouse';

export function isTruncated(el) {
	return !!el && el.scrollWidth > el.clientWidth;
}

/** Mark every truncated `.pvs-option` in an (open, laid out) list. */
export function decorateTruncated(list) {
	if (!list) return;
	var rows = list.querySelectorAll('.pvs-option');
	for (var i = 0; i < rows.length; i++) {
		var row = rows[i];
		var txt = row.querySelector('.pvs-opt-text');
		var old = row.querySelector('[data-pvs-info]');
		if (old) old.parentNode.removeChild(old);
		row.removeAttribute('title');
		row.removeAttribute('aria-label');
		row.removeAttribute('data-pvs-trunc');
		if (!isTruncated(txt)) continue;
		var full = row.getAttribute('data-pvs-value') || txt.textContent;
		row.setAttribute('aria-label', full);
		row.setAttribute('data-pvs-trunc', '1');
		var icon = row.ownerDocument.createElement('span');
		icon.setAttribute('data-pvs-info', '');
		icon.setAttribute('aria-hidden', 'true');
		icon.style.cssText = 'flex-shrink:0;display:inline-flex;align-items:center;justify-content:center;padding:4px;margin:-4px -4px -4px 4px;cursor:help;opacity:.85;color:inherit;';
		icon.innerHTML = ICON_INFO;
		var chk = row.querySelector('.pvs-opt-check');
		if (chk) row.insertBefore(icon, chk); else row.appendChild(icon);
	}
}

/** Give each closed field trigger a `title` when its selected value is cut off. */
export function markTriggerTitles(root, sel) {
	if (!root) return;
	var btns = root.querySelectorAll('[data-pvs-btn]');
	for (var i = 0; i < btns.length; i++) {
		var btn = btns[i];
		var id = btn.getAttribute('data-pvs-btn');
		var txt = btn.querySelector('.pvs-btn-text');
		if (sel && sel[id] && isTruncated(txt)) btn.setAttribute('title', String(sel[id]));
		else btn.removeAttribute('title');
	}
}

function tipEl(doc) {
	var tip = doc.getElementById(TIP_ID);
	if (tip) return tip;
	tip = doc.createElement('div');
	tip.id = TIP_ID;
	tip.setAttribute('role', 'tooltip');
	// Visual style = ResultsGrid MatchBadge tooltip (ALTERNATIVE PARTS badge).
	tip.style.cssText = 'position:fixed;top:0;left:0;display:none;visibility:hidden;'
		+ 'background:' + TIP_BG + ';color:#ffffff;font-size:11px;font-weight:500;line-height:1.35;'
		+ 'padding:6px 10px;border-radius:6px;white-space:normal;overflow-wrap:anywhere;text-align:center;'
		+ 'box-shadow:0 4px 12px rgba(0,0,0,0.18);text-transform:none;letter-spacing:0;'
		+ 'z-index:2147483647;pointer-events:none;box-sizing:border-box;';
	var text = doc.createElement('span');
	text.setAttribute('data-tip-text', '');
	var arrow = doc.createElement('span');
	arrow.setAttribute('data-tip-arrow', '');
	arrow.style.cssText = 'position:absolute;width:0;height:0;border-left:5px solid transparent;border-right:5px solid transparent;';
	tip.appendChild(text);
	tip.appendChild(arrow);
	(doc.body || doc.documentElement).appendChild(tip);
	return tip;
}

export function hideTip() {
	if (_anchor) {
		var tip = _anchor.ownerDocument.getElementById(TIP_ID);
		if (tip) tip.style.display = 'none';
	}
	_anchor = null;
	_via = null;
}

function positionTip() {
	if (!_anchor) return;
	var doc = _anchor.ownerDocument;
	var tip = doc.getElementById(TIP_ID);
	if (!tip) return;
	if (!_anchor.isConnected) { hideTip(); return; }
	var r = _anchor.getBoundingClientRect();
	var list = _anchor.closest('.pvs-options');
	if (list) {
		var lr = list.getBoundingClientRect();
		if (r.bottom < lr.top || r.top > lr.bottom || lr.height === 0) { hideTip(); return; }
	}
	var win = doc.defaultView || window;
	var vw = doc.documentElement.clientWidth || win.innerWidth;
	var vh = doc.documentElement.clientHeight || win.innerHeight;
	tip.style.maxWidth = Math.min(240, vw - EDGE * 2) + 'px';
	tip.style.minWidth = Math.min(180, vw - EDGE * 2) + 'px';
	var tw = tip.offsetWidth, th = tip.offsetHeight;
	var cx = r.left + r.width / 2;
	var left = Math.max(EDGE, Math.min(cx - tw / 2, vw - EDGE - tw));
	var above = r.top - OFFSET - th >= EDGE;
	var top = above ? r.top - OFFSET - th : r.bottom + OFFSET;
	if (!above && top + th > vh - EDGE) top = Math.max(EDGE, vh - EDGE - th);
	tip.style.left = left + 'px';
	tip.style.top = top + 'px';
	var arrow = tip.querySelector('[data-tip-arrow]');
	arrow.style.left = Math.max(6, Math.min(cx - left - 5, tw - 16)) + 'px';
	if (above) {
		arrow.style.top = '100%'; arrow.style.bottom = '';
		arrow.style.borderTop = '5px solid ' + TIP_BG; arrow.style.borderBottom = '';
	} else {
		arrow.style.top = ''; arrow.style.bottom = '100%';
		arrow.style.borderTop = ''; arrow.style.borderBottom = '5px solid ' + TIP_BG;
	}
	tip.style.visibility = 'visible';
}

/** Show the full-name tooltip for a row (no-op unless the row is truncated). */
export function showTipForRow(row, via) {
	var icon = row && row.getAttribute('data-pvs-trunc') ? row.querySelector('[data-pvs-info]') : null;
	if (!icon) { hideTip(); return; }
	var doc = icon.ownerDocument;
	var tip = tipEl(doc);
	tip.querySelector('[data-tip-text]').textContent = row.getAttribute('data-pvs-value') || '';
	tip.style.display = 'block';
	tip.style.visibility = 'hidden';
	_anchor = icon;
	_via = via;
	positionTip();
}

var _docBound = typeof WeakSet !== 'undefined' ? new WeakSet() : null;

/**
 * Wire hover / tap behaviour onto a freshly rendered widget root. Call after
 * every render (the root is rebuilt); document-level listeners bind once.
 */
export function bindTruncTips(root) {
	if (!root) return;
	var doc = root.ownerDocument;
	var win = doc.defaultView || window;

	// Mouse hover on a row (or its icon) shows the tooltip; leaving hides it.
	root.addEventListener('pointerover', function (e) {
		if (e.pointerType !== 'mouse') return;
		var row = e.target.closest && e.target.closest('.pvs-option');
		if (!row) return;
		if (_anchor && _anchor.closest('.pvs-option') === row) return;
		showTipForRow(row, 'mouse');
	});
	root.addEventListener('pointerout', function (e) {
		if (e.pointerType !== 'mouse' || _via !== 'mouse') return;
		var row = e.target.closest && e.target.closest('.pvs-option');
		if (row && e.relatedTarget && row.contains(e.relatedTarget)) return;
		hideTip();
	});

	// Icon: remember the pointer type and keep focus in the search input.
	root.addEventListener('pointerdown', function (e) {
		if (e.target.closest && e.target.closest('[data-pvs-info]')) _lastPointer = e.pointerType || 'mouse';
	}, true);
	root.addEventListener('mousedown', function (e) {
		if (e.target.closest && e.target.closest('[data-pvs-info]')) e.preventDefault();
	}, true);
	// Capture phase so the row's select handler never sees an icon click.
	root.addEventListener('click', function (e) {
		var icon = e.target.closest && e.target.closest('[data-pvs-info]');
		if (!icon) return;
		e.preventDefault();
		e.stopPropagation();
		var row = icon.closest('.pvs-option');
		if (_lastPointer !== 'mouse' && _anchor === icon && _via === 'touch') hideTip();
		else showTipForRow(row, _lastPointer === 'mouse' ? 'mouse' : 'touch');
	}, true);

	if (_docBound && _docBound.has(doc)) return;
	if (_docBound) _docBound.add(doc);
	// Tap / click anywhere else hides it.
	doc.addEventListener('pointerdown', function (e) {
		if (!_anchor) return;
		if (e.target.closest && e.target.closest('[data-pvs-info]')) return;
		if (_via === 'mouse' && e.target.closest && e.target.closest('.pvs-option')) return;
		hideTip();
	}, true);
	win.addEventListener('scroll', function () { if (_anchor) positionTip(); }, true);
	win.addEventListener('resize', function () { hideTip(); });
}
